import React, { useState, useEffect } from 'react';
import { AlertCircle, Camera, X, ChevronDown } from 'lucide-react';

const MaintenanceForm = ({ onSubmit, isLoading, assetUnits = [], initialData = null, isEdit = false, onCancel }) => {
    const [formData, setFormData] = useState({
        asset_id: '',
        asset_unit_id: '',
        title: '',
        description: '',
        priority: 'medium',
        photos: []
    });
    const [availableUnits, setAvailableUnits] = useState([]);
    const [assets, setAssets] = useState([]);
    const [error, setError] = useState('');

    // Group asset units by asset
    useEffect(() => {
        // Extract unique assets from assetUnits
        const uniqueAssets = [];
        const assetMap = new Map();
        
        assetUnits.forEach(unit => {
            if (!assetMap.has(unit.asset_id)) {
                assetMap.set(unit.asset_id, {
                    id: unit.asset_id,
                    name: unit.asset_name,
                    category: unit.category
                });
                uniqueAssets.push({
                    id: unit.asset_id,
                    name: unit.asset_name,
                    category: unit.category
                });
            }
        });
        
        setAssets(uniqueAssets);
    }, [assetUnits]);

    // Update available units when asset is selected
    useEffect(() => {
        if (formData.asset_id) {
            const units = assetUnits.filter(unit => unit.asset_id === parseInt(formData.asset_id));
            setAvailableUnits(units);
            
            // Reset unit selection when asset changes
            if (formData.asset_unit_id) {
                const stillValid = units.some(unit => unit.id === parseInt(formData.asset_unit_id));
                if (!stillValid) {
                    setFormData(prev => ({ ...prev, asset_unit_id: '' }));
                }
            }
        } else {
            setAvailableUnits([]);
            setFormData(prev => ({ ...prev, asset_unit_id: '' }));
        }
    }, [formData.asset_id, assetUnits]);

    // Initialize form with initialData when in edit mode
    useEffect(() => {
        if (initialData && isEdit) {
            // Find the asset_id from the unit
            const unit = assetUnits.find(u => u.id === initialData.asset_unit_id);
            setFormData({
                asset_id: unit?.asset_id?.toString() || '',
                asset_unit_id: initialData.asset_unit_id?.toString() || '',
                title: initialData.title || '',
                description: initialData.description || '',
                priority: initialData.priority || 'medium',
                photos: initialData.photos || []
            });
        }
    }, [initialData, isEdit, assetUnits]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        
        if (!formData.asset_unit_id || !formData.title || !formData.description) {
            setError('Please fill in all required fields');
            return;
        }
        
        try {
            // Send only the required fields for the API
            const submitData = {
                asset_unit_id: parseInt(formData.asset_unit_id),
                title: formData.title,
                description: formData.description,
                priority: formData.priority,
                photos: formData.photos
            };
            await onSubmit(submitData);
            
            // Reset form only for create mode, not for edit
            if (!isEdit) {
                setFormData({
                    asset_id: '',
                    asset_unit_id: '',
                    title: '',
                    description: '',
                    priority: 'medium',
                    photos: []
                });
                setAvailableUnits([]);
            }
        } catch (err) {
            setError(err.message || `Failed to ${isEdit ? 'update' : 'submit'} request`);
        }
    };

    const handlePhotoUpload = (e) => {
        const files = Array.from(e.target.files);
        const photoUrls = files.map(file => URL.createObjectURL(file));
        setFormData({ ...formData, photos: [...formData.photos, ...photoUrls] });
    };

    const removePhoto = (indexToRemove) => {
        setFormData({
            ...formData,
            photos: formData.photos.filter((_, index) => index !== indexToRemove)
        });
    };

    const resetForm = () => {
        setFormData({
            asset_id: '',
            asset_unit_id: '',
            title: '',
            description: '',
            priority: 'medium',
            photos: []
        });
        setAvailableUnits([]);
        setError('');
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
                <div className="p-3 bg-red-100 text-red-700 rounded-lg text-sm">
                    {error}
                </div>
            )}
            
            {/* Asset Selection */}
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    Asset *
                </label>
                <select
                    value={formData.asset_id}
                    onChange={(e) => setFormData({ ...formData, asset_id: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500"
                    required
                    disabled={isLoading}
                >
                    <option value="">Select an asset</option>
                    {assets.map(asset => (
                        <option key={asset.id} value={asset.id}>
                            {asset.name} ({asset.category})
                        </option>
                    ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">Select the property, vehicle, or equipment first</p>
            </div>
            
            {/* Unit Selection - Only shows after asset is selected */}
            {formData.asset_id && (
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Unit / Specific Item *
                    </label>
                    <select
                        value={formData.asset_unit_id}
                        onChange={(e) => setFormData({ ...formData, asset_unit_id: e.target.value })}
                        className="w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500"
                        required
                        disabled={isLoading || availableUnits.length === 0}
                    >
                        <option value="">Select a unit</option>
                        {availableUnits.map(unit => (
                            <option key={unit.id} value={unit.id}>
                                {unit.unit_number || unit.name || 'Main Unit'} 
                                {unit.rate_override && ` (Special Rate: KES ${unit.rate_override})`}
                            </option>
                        ))}
                    </select>
                    {availableUnits.length === 0 && (
                        <p className="text-xs text-yellow-600 mt-1">
                            No units available for this asset. Please add units first.
                        </p>
                    )}
                </div>
            )}
            
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    Issue Title *
                </label>
                <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500"
                    placeholder="e.g., Leaking faucet, AC not working"
                    required
                    disabled={isLoading}
                />
            </div>
            
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    Priority
                </label>
                <div className="grid grid-cols-4 gap-2">
                    {['low', 'medium', 'high', 'emergency'].map(priority => (
                        <button
                            key={priority}
                            type="button"
                            onClick={() => setFormData({ ...formData, priority })}
                            disabled={isLoading}
                            className={`p-2 rounded-lg text-sm capitalize transition-colors ${
                                formData.priority === priority
                                    ? priority === 'emergency'
                                        ? 'bg-red-600 text-white'
                                        : priority === 'high'
                                        ? 'bg-orange-600 text-white'
                                        : 'bg-indigo-600 text-white'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                        >
                            {priority}
                        </button>
                    ))}
                </div>
            </div>
            
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description *
                </label>
                <textarea
                    rows={4}
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500"
                    placeholder="Please provide detailed description of the issue..."
                    required
                    disabled={isLoading}
                />
            </div>
            
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    Photos (Optional)
                </label>
                <div className="flex items-center space-x-2">
                    <label className={`cursor-pointer bg-gray-100 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}>
                        <Camera className="h-5 w-5 inline mr-2" />
                        Upload Photos
                        <input
                            type="file"
                            multiple
                            accept="image/*"
                            onChange={handlePhotoUpload}
                            className="hidden"
                            disabled={isLoading}
                        />
                    </label>
                    <span className="text-sm text-gray-500">
                        {formData.photos.length} photo(s) selected
                    </span>
                    {formData.photos.length > 0 && (
                        <button
                            type="button"
                            onClick={() => setFormData({ ...formData, photos: [] })}
                            className="text-red-600 text-sm hover:text-red-800"
                            disabled={isLoading}
                        >
                            Clear all
                        </button>
                    )}
                </div>
                {formData.photos.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                        {formData.photos.map((photo, index) => (
                            <div key={index} className="relative group">
                                <img
                                    src={photo}
                                    alt={`Preview ${index + 1}`}
                                    className="h-20 w-20 object-cover rounded-lg border border-gray-200"
                                />
                                <button
                                    type="button"
                                    onClick={() => removePhoto(index)}
                                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                                    disabled={isLoading}
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>
            
            <div className="flex items-start p-3 bg-yellow-50 rounded-lg">
                <AlertCircle className="h-5 w-5 text-yellow-600 mr-2 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-yellow-800">
                    For emergency issues, please also call the property manager directly.
                </p>
            </div>
            
            <div className="flex space-x-3">
                <button
                    type="submit"
                    disabled={isLoading}
                    className="flex-1 bg-indigo-600 text-white py-2 rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50"
                >
                    {isLoading ? 'Processing...' : (isEdit ? 'Update Ticket' : 'Submit Maintenance Request')}
                </button>
                
                {isEdit && onCancel && (
                    <button
                        type="button"
                        onClick={onCancel}
                        disabled={isLoading}
                        className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                    >
                        Cancel
                    </button>
                )}
                
                {!isEdit && !isLoading && (
                    <button
                        type="button"
                        onClick={resetForm}
                        className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                        Clear
                    </button>
                )}
            </div>
        </form>
    );
};

export default MaintenanceForm;